import path from 'node:path';
import type {
  ResolveTsConfig,
  RspackPluginFunction,
  RuleSetRule,
  SwcLoaderOptions,
} from '@rspack/core';
import type { RuleInsertOptions } from './types.ts';
import { insertRule } from './utils.ts';

export interface PluginTypeScriptOptions extends RuleInsertOptions {
  /** Extensions, that will be added to `resolve.extensions`. */
  resolveExtensions?: string[] | false;

  /** TypeScript config file path, see `resolve.tsConfig` of Rspack configuration. */
  tsConfig?: ResolveTsConfig | false;

  /** Configuration for `builtin:swc-loader`. */
  swcLoaderOptions?: SwcLoaderOptions;

  /** When false passed, rule for TypeScript files will not be added to configuration. */
  ruleEnabled?: boolean;

  /** Rule extension/override. */
  ruleOverride?: Omit<RuleSetRule, 'use'>;
}

/**
 * Rspack plugin that adds support of TypeScript.
 * It adds rule for handling TypeScript source files.
 * It adds items to `resolve.extensions` in configuration (by default `.ts, .tsx` will be added).
 * It adds `resolve.tsConfig` with path to `tsconfig.json` in project root (when it is not provided in config).
 * By default it uses `automatic` React runtime in SWC config.
 * @param options Options.
 * @returns Plugin function.
 */
export function pluginTypeScript({
  resolveExtensions = ['.jsx', '.ts', '.tsx', '.mts', '.cts'],
  tsConfig = {
    configFile: path.resolve(process.cwd(), 'tsconfig.json'),
  },
  ruleEnabled = true,
  ruleInsert,
  ruleOverride,
  swcLoaderOptions,
}: PluginTypeScriptOptions = {}): RspackPluginFunction {
  return compiler => {
    // resolve.extensions option enhance
    // IMPORTANT: this is not in hook callback because:
    // https://github.com/webpack/webpack/discussions/19170#discussioncomment-12083008
    if (resolveExtensions !== false) {
      if (!compiler.options.resolve.extensions) {
        compiler.options.resolve.extensions = ['...'];
      }

      for (const ext of resolveExtensions) {
        if (!compiler.options.resolve.extensions.includes(ext)) {
          compiler.options.resolve.extensions.push(ext);
        }
      }
    }

    // `tsconfig.json` path to build config
    if (tsConfig !== false && compiler.options.resolve.tsConfig === undefined) {
      compiler.options.resolve.tsConfig = tsConfig;
    }

    compiler.hooks.afterEnvironment.tap('krutoo:pluginTypeScript', () => {
      if (ruleEnabled) {
        // rule for handling TypeScript source files
        // @todo переделать множественный spread на функцию которая получает дефолт и возвращает SwcLoaderOptions
        const ruleOptions: SwcLoaderOptions = {
          ...swcLoaderOptions,
          jsc: {
            ...swcLoaderOptions?.jsc,
            parser: swcLoaderOptions?.jsc?.parser ?? {
              syntax: 'typescript',
              tsx: true,
            },
            transform: {
              ...swcLoaderOptions?.jsc?.transform,
              react: {
                ...swcLoaderOptions?.jsc?.transform?.react,
                runtime: swcLoaderOptions?.jsc?.transform?.react?.runtime ?? 'automatic',
              },
            },
          },
        } as SwcLoaderOptions;

        const rule: RuleSetRule = {
          test: /\.(js|jsx|ts|tsx|mts|cts)$/i,
          exclude: /node_modules/,
          loader: 'builtin:swc-loader',
          options: ruleOptions,

          // IMPORTANT: `type: 'javascript/auto'` missed by default
          // because it not working correctly with pluginRawImport

          ...ruleOverride,
        };

        insertRule(rule, { ruleInsert }, compiler.options.module.rules);
      }
    });
  };
}
